import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Segmented,
  Space,
  Table,
  Typography,
  message,
} from 'antd'
import { Link } from 'react-router-dom'
import MaskedInput from 'react-input-mask'
import dayjs from 'dayjs'
import fetchAPI from '../../../../lib/utils/fetch-api'
import type { DataType, EnumElement } from './interface'

interface ManualRouteSheetModalProps {
  open: boolean
  routeSheet: DataType | null
  vehicles: EnumElement[]
  drivers: EnumElement[]
  centers: EnumElement[]
  onClose: () => void
  onSaved: () => void
}

interface DeliverySearchItem {
  id: number
  order_reference: string | null
  dealer: string | null
  customer_location_address: string | null
  delivery_date: string | null
  capacity: number | null
  volume: number | null
  cost: number | null
  glass_units_count: number | null
  delivery_amount?: number | null
  logistics_amount?: number | null
  linked_route_sheet_id?: number | null
  linked_route_sheet_date?: string | null
  linked_route_sheet_vehicle?: string | null
}

type RouteSheetFillMode = 'deliveries' | 'manual'

const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/

const TimeInput: React.FC<any> = props => (
  <MaskedInput {...props} mask="99:99" maskPlaceholder={null}>
    <Input inputMode="numeric" placeholder="19:16" style={{ width: '100%' }} />
  </MaskedInput>
)

const calculatedManualFields = [
  'manual_delivery_amount',
  'manual_orders_count',
  'manual_items_count',
  'manual_glass_units_count',
  'manual_weight',
  'manual_weight_fact',
  'manual_area',
  'manual_logistics_amount',
]

const numberFields = [
  'distance',
  'manual_delivery_amount',
  'manual_carrier_cost',
  'manual_orders_count',
  'manual_items_count',
  'manual_glass_units_count',
  'manual_weight',
  'manual_weight_fact',
  'manual_area',
  'manual_logistics_amount',
]

const normalizeTimeValue = (value: unknown): string | null => {
  if (typeof value !== 'string') return null

  const trimmedValue = value.trim()
  return trimmedValue || null
}

const extractLoadingTime = (value: unknown): string | null => {
  const normalizedValue = normalizeTimeValue(value)
  if (!normalizedValue) return null

  const match = normalizedValue.match(/([01]\d|2[0-3]):[0-5]\d/)
  return match?.[0] || normalizedValue
}

const formatLoadingTimeRange = (value: unknown): string | null => {
  const loadingTime = extractLoadingTime(value)
  if (!loadingTime) return null

  return timePattern.test(loadingTime) ? `${loadingTime} - ${loadingTime}` : loadingTime
}

const calculateTotalTravelTime = (
  loadingTimeValue: unknown,
  returnTimeValue: unknown,
): string | null => {
  const loadingTime = extractLoadingTime(loadingTimeValue)
  const returnTime = normalizeTimeValue(returnTimeValue)

  if (
    !loadingTime ||
    !returnTime ||
    !timePattern.test(loadingTime) ||
    !timePattern.test(returnTime)
  ) {
    return null
  }

  const [loadingHours, loadingMinutes] = loadingTime.split(':').map(Number)
  const [returnHours, returnMinutes] = returnTime.split(':').map(Number)
  const loadingTotalMinutes = loadingHours * 60 + loadingMinutes
  const returnTotalMinutes = returnHours * 60 + returnMinutes
  let difference = returnTotalMinutes - loadingTotalMinutes

  if (difference < 0) {
    difference += 24 * 60
  }

  const hours = Math.floor(difference / 60)
  const minutes = difference % 60

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

const buildRouteSheetPayload = (values: Record<string, any>, fillMode: RouteSheetFillMode) => {
  const returnTime = normalizeTimeValue(values.return_time)
  const payload = {
    ...values,
    date: values.date ? dayjs(values.date).format('YYYY-MM-DD') : null,
    loading_time: formatLoadingTimeRange(values.loading_time),
    return_time: returnTime,
    total_travel_time: calculateTotalTravelTime(values.loading_time, returnTime),
  }

  numberFields.forEach(field => {
    payload[field] = payload[field] === undefined || payload[field] === '' ? null : payload[field]
  })

  if (fillMode === 'deliveries') {
    calculatedManualFields.forEach(field => {
      payload[field] = null
    })
  }

  return payload
}

const hasManualCalculatedValues = (routeSheet: DataType): boolean =>
  calculatedManualFields.some(field => {
    const value = routeSheet[field as keyof DataType]
    return value !== null && value !== undefined && value !== ''
  })

const sumNumber = (items: DeliverySearchItem[], field: keyof DeliverySearchItem): number =>
  items.reduce((acc, item) => acc + (Number(item[field]) || 0), 0)

const getDeliveryDateValue = (value: unknown): string | null => {
  if (!value) return null

  const parsedDate = dayjs(value as any)
  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null
}

const attachDeliveries = async (
  routeSheetId: number | string,
  deliveryIds: number[],
  force = false,
) => {
  const response = await fetch(`/api/logistics/route-sheets/${routeSheetId}/deliveries`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ delivery_ids: deliveryIds, force }),
  })
  const result = await response.json()

  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      result,
    }
  }

  return {
    ok: true,
    status: response.status,
    result,
  }
}

export const ManualRouteSheetModal: React.FC<ManualRouteSheetModalProps> = ({
  open,
  routeSheet,
  vehicles,
  drivers,
  centers,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm()
  const [currentRouteSheet, setCurrentRouteSheet] = useState<DataType | null>(routeSheet)
  const [saving, setSaving] = useState(false)
  const [attachedDeliveries, setAttachedDeliveries] = useState<DeliverySearchItem[]>([])
  const [initialAttachedDeliveryIds, setInitialAttachedDeliveryIds] = useState<number[]>([])
  const [availableDeliveries, setAvailableDeliveries] = useState<DeliverySearchItem[]>([])
  const [attachedLoading, setAttachedLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)
  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState<React.Key[]>([])
  const [fillMode, setFillMode] = useState<RouteSheetFillMode>('manual')
  const [deliveryTablePage, setDeliveryTablePage] = useState(1)
  const [deliveryTablePageSize, setDeliveryTablePageSize] = useState(10)
  const routeDateValue = Form.useWatch('date', form)
  const loadingTimeValue = Form.useWatch('loading_time', form)
  const returnTimeValue = Form.useWatch('return_time', form)

  const routeSheetId = currentRouteSheet?.id
  const isEditing = Boolean(routeSheet?.id)

  const vehicleOptions = useMemo(
    () =>
      vehicles.map(vehicle => ({
        label: vehicle.label,
        value: vehicle.value,
      })),
    [vehicles],
  )

  const driverOptions = useMemo(
    () =>
      drivers.map(driver => ({
        label: driver.label,
        value: driver.value,
      })),
    [drivers],
  )

  const centerOptions = useMemo(
    () =>
      centers.map(center => ({
        label: center.label,
        value: center.value,
      })),
    [centers],
  )

  const selectedDeliveries = useMemo(() => {
    const deliveriesById = new Map<number, DeliverySearchItem>()

    availableDeliveries.forEach(delivery => {
      deliveriesById.set(delivery.id, delivery)
    })
    attachedDeliveries.forEach(delivery => {
      deliveriesById.set(delivery.id, delivery)
    })

    return selectedDeliveryIds
      .map(id => deliveriesById.get(Number(id)))
      .filter((delivery): delivery is DeliverySearchItem => Boolean(delivery))
  }, [attachedDeliveries, availableDeliveries, selectedDeliveryIds])

  const attachedTotals = useMemo(
    () => ({
      ordersCount: selectedDeliveries.length,
      itemsCount: sumNumber(selectedDeliveries, 'cost'),
      glassUnitsCount: sumNumber(selectedDeliveries, 'glass_units_count'),
      weight: sumNumber(selectedDeliveries, 'capacity'),
      area: sumNumber(selectedDeliveries, 'volume'),
      deliveryAmount: sumNumber(selectedDeliveries, 'delivery_amount'),
      logisticsAmount: sumNumber(selectedDeliveries, 'logistics_amount'),
    }),
    [selectedDeliveries],
  )

  const loadAttachedDeliveries = useCallback(
    async (targetRouteSheetId?: number | string) => {
      const targetId = targetRouteSheetId || routeSheetId

      if (!targetId) {
        setAttachedDeliveries([])
        return
      }

      setAttachedLoading(true)
      try {
        const response = await fetchAPI(`/api/logistics/route-sheets/${targetId}`)
        const deliveries = response.data || []
        setAttachedDeliveries(deliveries)
        setInitialAttachedDeliveryIds(deliveries.map((delivery: DeliverySearchItem) => delivery.id))
        setSelectedDeliveryIds(deliveries.map((delivery: DeliverySearchItem) => delivery.id))
      } catch (error) {
        console.error('Ошибка загрузки доставок маршрутного листа:', error)
        message.error('Не удалось загрузить доставки')
      } finally {
        setAttachedLoading(false)
      }
    },
    [routeSheetId],
  )

  const loadAvailableDeliveries = useCallback(async () => {
    const deliveryDate = getDeliveryDateValue(routeDateValue)

    if (!deliveryDate || fillMode !== 'deliveries') {
      setAvailableDeliveries([])
      return
    }

    setSearchLoading(true)
    try {
      const params = new URLSearchParams({
        delivery_date: deliveryDate,
        limit: '300',
      })

      if (searchValue.trim()) {
        params.set('search', searchValue.trim())
      }

      const response = await fetchAPI(
        `/api/logistics/route-sheets/deliveries/search?${params.toString()}`,
      )
      setAvailableDeliveries(response.data || [])
    } catch (error) {
      console.error('Ошибка загрузки доставок по дате:', error)
      message.error('Не удалось загрузить доставки')
    } finally {
      setSearchLoading(false)
    }
  }, [fillMode, routeDateValue, searchValue])

  useEffect(() => {
    if (!open) {
      return
    }

    const nextRouteSheet = routeSheet || null
    setCurrentRouteSheet(nextRouteSheet)
    setSearchValue('')
    setAvailableDeliveries([])

    if (nextRouteSheet) {
      setFillMode(hasManualCalculatedValues(nextRouteSheet) ? 'manual' : 'deliveries')
      form.setFieldsValue({
        date: nextRouteSheet.date ? dayjs(nextRouteSheet.date) : null,
        vehicle_id: nextRouteSheet.vehicle_id,
        driver_name: nextRouteSheet.driver_name,
        center_name: nextRouteSheet.center_name,
        distance: nextRouteSheet.distance,
        loading_time: extractLoadingTime(nextRouteSheet.loading_time),
        total_travel_time: nextRouteSheet.total_travel_time,
        return_time: nextRouteSheet.return_time,
        manual_delivery_amount:
          nextRouteSheet.manual_delivery_amount ?? nextRouteSheet.total_delivery_amount,
        manual_carrier_cost: nextRouteSheet.manual_carrier_cost ?? nextRouteSheet.carrier_cost,
        manual_orders_count: nextRouteSheet.manual_orders_count ?? nextRouteSheet.orders_count,
        manual_items_count: nextRouteSheet.manual_items_count ?? nextRouteSheet.total_items_count,
        manual_glass_units_count:
          nextRouteSheet.manual_glass_units_count ?? nextRouteSheet.total_glass_units_count,
        manual_weight: nextRouteSheet.manual_weight ?? nextRouteSheet.total_weight,
        manual_weight_fact: nextRouteSheet.manual_weight_fact ?? nextRouteSheet.total_weight_fact,
        manual_area: nextRouteSheet.manual_area ?? nextRouteSheet.total_volume,
        manual_logistics_amount:
          nextRouteSheet.manual_logistics_amount ?? nextRouteSheet.total_logistics_amount,
        comment: nextRouteSheet.comment,
      })
    } else {
      setFillMode('manual')
      form.resetFields()
      form.setFieldsValue({
        date: dayjs(),
      })
      setAttachedDeliveries([])
      setInitialAttachedDeliveryIds([])
      setSelectedDeliveryIds([])
    }
  }, [form, open, routeSheet])

  useEffect(() => {
    if (!open) {
      return
    }

    const calculatedTravelTime = calculateTotalTravelTime(loadingTimeValue, returnTimeValue)
    const currentTravelTime = form.getFieldValue('total_travel_time')

    if (calculatedTravelTime && currentTravelTime !== calculatedTravelTime) {
      form.setFieldsValue({ total_travel_time: calculatedTravelTime })
      return
    }

    if (!calculatedTravelTime && currentTravelTime) {
      form.setFieldsValue({ total_travel_time: null })
    }
  }, [form, loadingTimeValue, open, returnTimeValue])

  useEffect(() => {
    if (open && routeSheetId) {
      loadAttachedDeliveries()
    }
  }, [loadAttachedDeliveries, open, routeSheetId])

  useEffect(() => {
    if (open) {
      setDeliveryTablePage(1)
      loadAvailableDeliveries()
    }
  }, [loadAvailableDeliveries, open])

  const syncSelectedDeliveries = useCallback(
    async (savedRouteSheetId: number | string) => {
      if (fillMode !== 'deliveries') {
        return
      }

      const selectedIds = selectedDeliveryIds.map(id => Number(id))
      const selectedIdsSet = new Set(selectedIds)
      const initialIdsSet = new Set(initialAttachedDeliveryIds)
      const idsToAttach = selectedIds.filter(id => !initialIdsSet.has(id))
      const idsToDetach = initialAttachedDeliveryIds.filter(id => !selectedIdsSet.has(id))

      await Promise.all(
        idsToDetach.map(deliveryId =>
          fetchAPI(`/api/logistics/route-sheets/${savedRouteSheetId}/deliveries/${deliveryId}`, {
            method: 'DELETE',
          }),
        ),
      )

      if (!idsToAttach.length) {
        return
      }

      const response = await attachDeliveries(savedRouteSheetId, idsToAttach)

      if (response.ok) {
        return
      }

      if (response.status === 409) {
        const conflict = response.result?.conflicts?.[0]
        Modal.confirm({
          title: 'Есть доставки из других маршрутных листов',
          content: conflict
            ? `Доставка уже находится в маршрутном листе #${conflict.route_sheet_id}${
                conflict.vehicle ? `, машина ${conflict.vehicle}` : ''
              }. Перенести выбранные доставки в этот ручной лист?`
            : 'Одна или несколько доставок уже находятся в других маршрутных листах. Перенести выбранные доставки в этот ручной лист?',
          okText: 'Перенести',
          cancelText: 'Оставить как есть',
          onOk: async () => {
            const forceResponse = await attachDeliveries(savedRouteSheetId, idsToAttach, true)
            if (!forceResponse.ok) {
              throw new Error(
                forceResponse.result?.errors?.join(', ') || 'Не удалось перенести доставки',
              )
            }
            await loadAttachedDeliveries(savedRouteSheetId)
            onSaved()
          },
        })
        return
      }

      throw new Error(response.result?.errors?.join(', ') || 'Не удалось прикрепить доставки')
    },
    [fillMode, initialAttachedDeliveryIds, loadAttachedDeliveries, onSaved, selectedDeliveryIds],
  )

  const handleSave = useCallback(async () => {
    const values = await form.validateFields()
    const payload = buildRouteSheetPayload(values, fillMode)

    setSaving(true)
    try {
      const response = routeSheetId
        ? await fetchAPI(`/api/logistics/route-sheets/${routeSheetId}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
          })
        : await fetchAPI('/api/logistics/route-sheets', {
            method: 'POST',
            body: JSON.stringify(payload),
          })

      setCurrentRouteSheet(response.data as DataType)
      await syncSelectedDeliveries(response.data.id)
      await loadAttachedDeliveries(response.data.id)
      message.success(routeSheetId ? 'Маршрутный лист обновлен' : 'Маршрутный лист создан')
      onSaved()
    } catch (error) {
      console.error('Ошибка сохранения ручного маршрутного листа:', error)
      message.error('Не удалось сохранить маршрутный лист')
    } finally {
      setSaving(false)
    }
  }, [fillMode, form, loadAttachedDeliveries, onSaved, routeSheetId, syncSelectedDeliveries])

  const deliveryTableData = useMemo(() => {
    const deliveriesById = new Map<number, DeliverySearchItem>()

    selectedDeliveries.forEach(delivery => deliveriesById.set(delivery.id, delivery))
    availableDeliveries.forEach(delivery => deliveriesById.set(delivery.id, delivery))

    return Array.from(deliveriesById.values()).sort((left, right) => {
      const leftDate = left.delivery_date ? dayjs(left.delivery_date).valueOf() : 0
      const rightDate = right.delivery_date ? dayjs(right.delivery_date).valueOf() : 0

      if (leftDate !== rightDate) return leftDate - rightDate
      return Number(left.id) - Number(right.id)
    })
  }, [availableDeliveries, selectedDeliveries])

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(deliveryTableData.length / deliveryTablePageSize))

    if (deliveryTablePage > maxPage) {
      setDeliveryTablePage(maxPage)
    }
  }, [deliveryTableData.length, deliveryTablePage, deliveryTablePageSize])

  const deliveryColumns = useMemo(
    () => [
      {
        title: 'Номер ТЗ',
        dataIndex: 'order_reference',
        key: 'order_reference',
        width: 105,
        render: (value: string, record: DeliverySearchItem) => (
          <Link to={`/logistics/deliveries/${record.id}`}>{value || record.id}</Link>
        ),
      },
      {
        title: 'Машина',
        dataIndex: 'vehicle',
        key: 'vehicle',
        width: 120,
      },
      {
        title: 'Дилер',
        dataIndex: 'dealer',
        key: 'dealer',
        width: 130,
        ellipsis: true,
      },
      {
        title: 'Адрес',
        dataIndex: 'customer_location_address',
        key: 'customer_location_address',
        width: 260,
        ellipsis: true,
      },
      {
        title: 'Изд.',
        dataIndex: 'cost',
        key: 'cost',
        width: 55,
      },
      {
        title: 'Вес',
        dataIndex: 'capacity',
        key: 'capacity',
        width: 70,
      },
      {
        title: 'Площадь',
        dataIndex: 'volume',
        key: 'volume',
        width: 75,
      },
    ],
    [],
  )

  return (
    <Modal
      title={isEditing ? 'Редактирование ручного маршрутного листа' : 'Создание маршрутного листа'}
      open={open}
      onCancel={onClose}
      width={1180}
      footer={
        <Space>
          <Button onClick={onClose}>Закрыть</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Сохранить
          </Button>
        </Space>
      }
      destroyOnClose
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Segmented
          value={fillMode}
          onChange={value => setFillMode(value as RouteSheetFillMode)}
          options={[
            { label: 'Вручную', value: 'manual' },
            { label: 'По доставкам', value: 'deliveries' },
          ]}
        />

        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Typography.Title level={5}>Данные маршрутного листа</Typography.Title>
            </Col>
            <Col span={6}>
              <Form.Item
                name="date"
                label="Дата поездки"
                rules={[{ required: true, message: 'Укажите дату поездки' }]}
              >
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="vehicle_id"
                label="Машина"
                rules={[{ required: true, message: 'Выберите машину' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={vehicleOptions}
                  placeholder="Выберите машину"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="driver_name" label="Водитель">
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={driverOptions}
                  placeholder="Выберите водителя"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="center_name" label="Склад">
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={centerOptions}
                  placeholder="Выберите склад"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="distance" label="Расстояние, км">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="loading_time"
                label="Загрузка"
                rules={[{ pattern: timePattern, message: 'Введите время в формате 19:16' }]}
              >
                <TimeInput />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="return_time"
                label="Возвращение"
                rules={[{ pattern: timePattern, message: 'Введите время в формате 19:16' }]}
              >
                <TimeInput />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="total_travel_time" label="Суммарное время">
                <Input placeholder="Автоматически" disabled />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="comment" label="Комментарий">
                <Input.TextArea rows={1} />
              </Form.Item>
            </Col>

            {fillMode === 'manual' && (
              <>
                <Col span={24}>
                  <Typography.Title level={5}>Финансы</Typography.Title>
                </Col>
                <Col span={8}>
                  <Form.Item name="manual_carrier_cost" label="Оплата перевозчику, ₽">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="manual_delivery_amount" label="Оплата клиентов, ₽">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="manual_logistics_amount" label="Затраты логистики, ₽">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Typography.Title level={5}>Итоги рейса</Typography.Title>
                </Col>
                <Col span={6}>
                  <Form.Item name="manual_orders_count" label="Кол-во заказов">
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="manual_items_count" label="Кол-во изделий">
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="manual_glass_units_count" label="Кол-во стеклопакетов">
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="manual_weight" label="Вес рассчитанный, кг">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="manual_weight_fact" label="Фактический вес, кг">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="manual_area" label="Площадь, м²">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </>
            )}

            {fillMode === 'deliveries' && (
              <>
                <Col span={24}>
                  <Typography.Title level={5}>Финансы</Typography.Title>
                </Col>
                <Col span={8}>
                  <Form.Item name="manual_carrier_cost" label="Оплата перевозчику, ₽">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Оплата клиентов, ₽">
                    <InputNumber
                      value={attachedTotals.deliveryAmount}
                      disabled
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Затраты логистики, ₽">
                    <InputNumber
                      value={attachedTotals.logisticsAmount}
                      disabled
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Typography.Title level={5}>Итоги рейса</Typography.Title>
                </Col>
                <Col span={6}>
                  <Form.Item label="Кол-во заказов">
                    <InputNumber
                      value={attachedTotals.ordersCount}
                      disabled
                      precision={0}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Кол-во изделий">
                    <InputNumber
                      value={attachedTotals.itemsCount}
                      disabled
                      precision={0}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Кол-во стеклопакетов">
                    <InputNumber
                      value={attachedTotals.glassUnitsCount}
                      disabled
                      precision={0}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Вес рассчитанный, кг">
                    <InputNumber value={attachedTotals.weight} disabled style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Фактический вес, кг">
                    <InputNumber value={attachedTotals.weight} disabled style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Площадь, м²">
                    <InputNumber value={attachedTotals.area} disabled style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </Form>

        {fillMode === 'deliveries' && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Typography.Title level={5}>Доставки за дату поездки</Typography.Title>
            <Input.Search
              value={searchValue}
              placeholder="Фильтр по номеру заказа, дилеру или адресу"
              enterButton="Найти"
              loading={searchLoading}
              allowClear
              onChange={event => setSearchValue(event.target.value)}
              onSearch={loadAvailableDeliveries}
            />
            {!getDeliveryDateValue(routeDateValue) ? (
              <Alert
                type="info"
                showIcon
                message="Выберите дату поездки, чтобы увидеть доставки."
              />
            ) : (
              <Table
                columns={deliveryColumns}
                dataSource={deliveryTableData}
                rowKey="id"
                size="small"
                tableLayout="fixed"
                loading={attachedLoading || searchLoading}
                rowSelection={{
                  columnWidth: 36,
                  selectedRowKeys: selectedDeliveryIds,
                  onChange: keys => setSelectedDeliveryIds(keys),
                }}
                pagination={{
                  current: deliveryTablePage,
                  pageSize: deliveryTablePageSize,
                  size: 'small',
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  onChange: (page, pageSize) => {
                    setDeliveryTablePage(pageSize === deliveryTablePageSize ? page : 1)
                    setDeliveryTablePageSize(pageSize)
                  },
                }}
                bordered
              />
            )}
          </Space>
        )}
      </Space>
    </Modal>
  )
}
